import { type ChatSession, deleteChatSession } from "@/api/chat";
import ChatConfigDialog from "@/components/chats/chat-config-dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useAppContext } from "@/providers/app-context";
import { useQueryClient } from "@tanstack/react-query";
import { Trash2Icon } from "lucide-react";

export const ChatSessionHeaderActions = ({
  chatSession,
  onDeleted,
  compact = false,
}: {
  chatSession: ChatSession;
  onDeleted?: () => void;
  /** When true, render Delete as an icon-only button. */
  compact?: boolean;
}) => {
  const { useConfirmation } = useAppContext();
  const confirmation = useConfirmation();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const handleDelete = () =>
    confirmation.open({
      title: "Delete chat session",
      description:
        "Are you sure you want to delete this chat session? All messages will be lost.",
      confirmText: "Delete chat session",
      callback: async () => {
        await deleteChatSession(chatSession.id);
        toast({ title: "Chat session deleted" });
        queryClient.invalidateQueries({ queryKey: ["chat-sessions"] });
        onDeleted?.();
      },
    });

  return (
    <>
      <ChatConfigDialog
        chatSession={chatSession}
        variant="outline"
        size={compact ? "sm" : "default"}
      >
        Config
      </ChatConfigDialog>
      {compact ? (
        <Button
          variant="ghost"
          size="icon"
          title="Delete chat"
          onClick={handleDelete}
        >
          <Trash2Icon className="h-4 w-4" />
        </Button>
      ) : (
        <Button variant="destructive" onClick={handleDelete}>
          <div className="flex">
            <Trash2Icon size="20" />
            <span className="ml-1 hidden xl:block">Delete chat</span>
          </div>
        </Button>
      )}
    </>
  );
};
